import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/reducers/user';
import axios from 'axios';
import { BASE_URL } from '../utils/BaseUrl';
import { getFirstAuthorizedRoute } from '../utils/rbac';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BlockIcon from '@mui/icons-material/Block';
import FilterListIcon from '@mui/icons-material/FilterList';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import logolight from "../assets/images/logo1.png";
import googleIcon from "../assets/images/google_icon.png";
import medgeDarkBgSidebarF from "../assets/images/medge_dark_bg-sidebarf.webp";

export default function LoginBoxLikeFlutter() {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [typedIdentifier, setTypedIdentifier] = useState("");
  const [resolvedIdentifier, setResolvedIdentifier] = useState("");
  const [authMethod, setAuthMethod] = useState("password");
  const [password, setPassword] = useState("");
  const [identifyLoading, setIdentifyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Forced password change state (kept in component state only, never persisted)
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changeToken, setChangeToken] = useState(null);
  const [prefillEmail, setPrefillEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");

  const methods = useForm();

  useEffect(() => {
    document.title = "mAuthenticate MFA - Modern Multi-Factor Authentication System";
  }, []);

  // Restore remembered identifier and rememberMe state if saved
  useEffect(() => {
    try {
      const savedRemember = localStorage.getItem('rememberMe') === 'true';
      const savedIdentifier = localStorage.getItem('rememberedIdentifier');
      if (savedRemember && savedIdentifier) {
        setRememberMe(true);
        setTypedIdentifier(savedIdentifier);
        methods.setValue('email', savedIdentifier);
      }
    } catch {
      // ignore
    }
  }, [methods]);

  useEffect(() => {
    if (prefillEmail) {
      setTypedIdentifier(prefillEmail);
      methods.setValue('email', prefillEmail);
    }
  }, [prefillEmail, methods]);

  // Step 1: Submit identifier to /auth/identify
  const onStep1Submit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const raw = typedIdentifier || "";
    if (!raw.trim()) {
      setErrorMessage("Enter your email or username.");
      return;
    }

    setIdentifyLoading(true);
    try {
      // Never send the password to identify; never log identifier with password
      const response = await axios.post(`${BASE_URL}auth/identify`, {
        identifier: raw,
      });

      if (response && response.status === 200) {
        // After a 200 from identify, use the identifier returned by identify (the trimmed value)
        const returnedId = response.data?.identifier || raw.trim();
        const returnedMethod = response.data?.method === 'directory' ? 'directory' : 'password';
        setResolvedIdentifier(returnedId);
        setAuthMethod(returnedMethod);
        setPassword("");
        setStep(2);
      } else {
        setResolvedIdentifier(raw.trim());
        setAuthMethod('password');
        setPassword("");
        setStep(2);
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        // 429 means stay on step 1 with 'Too many attempts. Try again later.'
        setErrorMessage("Too many attempts. Try again later.");
      } else if (status === 400) {
        // 400 means show the server message
        setErrorMessage(error.response?.data?.message || "Enter your email or username.");
      } else if (!status || status === 404 || status >= 500) {
        // network error, 5xx or 404 (older backend) means go to step 2 anyway with method 'password'
        setResolvedIdentifier(raw.trim());
        setAuthMethod('password');
        setPassword("");
        setStep(2);
      } else {
        setResolvedIdentifier(raw.trim());
        setAuthMethod('password');
        setPassword("");
        setStep(2);
      }
    } finally {
      setIdentifyLoading(false);
    }
  };

  // Back to step 1: clear password and reset step
  const handleBackToStep1 = () => {
    setPassword("");
    setErrorMessage("");
    setStep(1);
  };

  // Step 2: Submit credentials to /auth/login
  const onStep2Submit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    // Submitting calls the existing auth/login with the same payload keys the page uses today (email, password)
    // and uses the identifier returned by identify (the trimmed value), not the raw typed text
    const loginPayload = {
      email: resolvedIdentifier,
      password: password,
      ...(rememberMe ? { rememberMe: true } : {}),
    };

    try {
      const response = await axios.post(`${BASE_URL}auth/login`, loginPayload);
      const ok = response?.data?.success || !!response?.data?.session_Id;
      if (ok) {
        if (rememberMe) {
          localStorage.setItem('rememberedIdentifier', resolvedIdentifier);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedIdentifier');
          localStorage.removeItem('rememberMe');
        }

        const storage = rememberMe ? localStorage : sessionStorage;
        const alternateStorage = rememberMe ? sessionStorage : localStorage;
        alternateStorage.removeItem('user');
        alternateStorage.removeItem('token');
        alternateStorage.removeItem('loginTime');

        if (response.status === 202 || response.data?.mfaRequired) {
          if (response.data?.user) {
            storage.setItem('user', JSON.stringify(response.data.user));
            dispatch(setUser(response.data.user));
          }
          if (response.data?.token) {
            storage.setItem('token', response.data.token);
          }
          setSuccessMessage(response.data?.message || 'MFA verification required.');
          return;
        }

        const user = response.data.user;
        const appToken = response.data.token || response.data.session_Id || "";
        storage.setItem('user', JSON.stringify(user));
        storage.setItem('token', appToken);
        storage.setItem('loginTime', new Date().toISOString());
        dispatch(setUser(user));

        if (response.data?.resume === true && typeof response.data.resumeUrl === 'string' && response.data.resumeUrl.trim() !== '') {
          window.location.href = response.data.resumeUrl;
          return;
        }

        const targetRoute = getFirstAuthorizedRoute(user);
        window.location.href = targetRoute;
      } else {
        const msg = response.data.message || 'Something went wrong';
        setErrorMessage(msg);
      }
    } catch (error) {
      const status = error.response?.status;
      const code = error.response?.data?.code;
      if (status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') {
        const token = error.response?.data?.changeToken;
        setChangeToken(token);
        setPrefillEmail(resolvedIdentifier || '');
        setShowChangePassword(true);
        setNewPassword('');
        setConfirmPassword('');
        setChangePasswordError('');
        setErrorMessage('');
        setSuccessMessage('');
        return;
      }

      if (status === 403 && (code === 'ACCOUNT_LOCKED' || error.response?.data?.message === 'ACCOUNT_LOCKED')) {
        const msg = (error.response?.data?.message && error.response.data.message !== 'ACCOUNT_LOCKED')
          ? error.response.data.message
          : 'Your account is locked. Contact your administrator.';
        setErrorMessage(msg);
        setPassword('');
        setStep(2);
        return;
      }

      // A wrong password (401, etc.) keeps you on step 2 with the error and the identifier
      const msg = error.response?.data?.message || 'Something went wrong';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };


  const onChangePasswordSubmitHandler = async (e) => {
    e.preventDefault();
    setChangePasswordError('');

    if (!newPassword || !confirmPassword) {
      setChangePasswordError('Please enter and confirm your new password.');
      return;
    }
    if (newPassword.length < 12) {
      setChangePasswordError('Password must be at least 12 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePasswordError('Passwords do not match.');
      return;
    }

    setChangePasswordLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}auth/change-password`, {
        changeToken,
        newPassword
      });

      if (response.status === 200 || response.data?.success) {
        // Clear changeToken and return to login view with email prefilled
        setChangeToken(null);
        setNewPassword('');
        setConfirmPassword('');
        setShowChangePassword(false);
        setSuccessMessage('Password updated. Please sign in again.');
        setTypedIdentifier(prefillEmail);
        setPassword('');
        setStep(1);
        methods.setValue('email', prefillEmail);
        methods.setValue('password', '');
      } else {
        setChangePasswordError(response.data?.message || 'Something went wrong.');
      }
    } catch (error) {
      const status = error.response?.status;
      const code = error.response?.data?.code;
      if (status === 401 && code === 'CHANGE_TOKEN_INVALID') {
        setChangePasswordError('This request expired. Please sign in again.');
      } else if (status === 429) {
        setChangePasswordError('Too many attempts. Try again later.');
      } else if (status === 400) {
        setChangePasswordError(error.response?.data?.message || 'Invalid password request.');
      } else {
        setChangePasswordError(error.response?.data?.message || 'Something went wrong.');
      }
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleCancelChangePassword = () => {
    setChangeToken(null);
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordError('');
    setShowChangePassword(false);
    setTypedIdentifier(prefillEmail);
    setPassword('');
    setStep(1);
    methods.setValue('email', prefillEmail);
    methods.setValue('password', '');
  };

  const handleGoogle = () => window.location.href = `${BASE_URL}auth/oauth/google`;

  const inputSx = {
    mb: 1.5,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#E8F0FE',
      '& fieldset': { borderColor: '#E4E9F0' },
      '&:hover fieldset': { borderColor: '#CBD5E1' },
      '&.Mui-focused fieldset': { borderColor: '#1F6FEB' },
    },
    '& .MuiInputBase-input': {
      color: '#000000',
      py: '14px',
      fontSize: '15px',
      fontFamily: 'Arial, sans-serif',
      '&:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px #E8F0FE inset',
        WebkitTextFillColor: '#000000',
        transition: 'background-color 5000s ease-in-out 0s',
      },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, backgroundColor: '#ffffff', overflowX: 'hidden' }}>
      {/* Left side: Login Form */}
      <Box
        sx={{
          flex: { xs: '1', md: '0 0 42%', lg: '0 0 38%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: { xs: 4, sm: 6, md: 8 },
          backgroundColor: '#ffffff'
        }}
      >
        <Box sx={{ maxWidth: 380, width: '100%', mx: 'auto' }}>
          {/* Enhanced Logo on top of login form */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-start' }}>
            <img
              src={logolight}
              alt="mAuthenticate"
              style={{
                width: '100%',
                maxWidth: '280px',
                height: 'auto',
                display: 'block'
              }}
            />
          </Box>

          {showChangePassword ? (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: '#0B2545',
                  mb: 1,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                Set a new password
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748B',
                  mb: 2.5,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                Your account requires a password change before you can sign in.
              </Typography>

              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2.5
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.5 }}>
                  Password rules:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#64748B', fontSize: '13px', lineHeight: 1.6 }}>
                  <li>At least 12 characters</li>
                  <li>Different from the current password</li>
                  <li>Different from the default password</li>
                </Box>
              </Box>

              <form onSubmit={onChangePasswordSubmitHandler}>
                <TextField
                  placeholder="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  fullWidth
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setChangePasswordError('');
                  }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <TextField
                  placeholder="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setChangePasswordError('');
                  }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                {changePasswordError && (
                  <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {changePasswordError}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={changePasswordLoading}
                  sx={{
                    backgroundColor: '#0B2545',
                    color: '#FFFFFF',
                    py: 1.5,
                    mb: 1.5,
                    '&:hover': { backgroundColor: '#06152B' },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'Arial, sans-serif',
                    borderRadius: '50px',
                    boxShadow: 'rgba(0, 0, 0, 0.5) 0px 4px 18px 0px'
                  }}
                >
                  {changePasswordLoading ? 'Updating password...' : 'Update Password'}
                </Button>

                <Button
                  type="button"
                  fullWidth
                  variant="text"
                  onClick={handleCancelChangePassword}
                  sx={{
                    color: '#64748B',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': { backgroundColor: '#F1F5F9', color: '#0B2545' }
                  }}
                >
                  Cancel & Back to Sign In
                </Button>
              </form>
            </Box>
          ) : (
            <>
              {successMessage && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    p: 1.5,
                    backgroundColor: '#ECFDF5',
                    border: '1px solid #A7F3D0',
                    borderRadius: '8px',
                    color: '#065F46',
                    fontWeight: 500
                  }}
                >
                  {successMessage}
                </Typography>
              )}

              {step === 1 ? (
                <>
                  <form onSubmit={onStep1Submit}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 0.75,
                        color: '#334155',
                        fontWeight: 500,
                        fontFamily: 'Arial, sans-serif'
                      }}
                    >
                      Email, username or phone
                    </Typography>
                    <TextField
                      placeholder="Email, username or phone"
                      aria-label="Email, username or phone"
                      fullWidth
                      size="small"
                      value={typedIdentifier}
                      onChange={(e) => setTypedIdentifier(e.target.value)}
                      inputProps={{
                        'aria-label': 'Email, username or phone',
                        autoComplete: 'username',
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlinedIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1F6FEB' } }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial, sans-serif' }}>
                            Remember me
                          </Typography>
                        }
                      />
                    </Stack>

                    {errorMessage && (
                      <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                        {errorMessage}
                      </Typography>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={identifyLoading}
                      sx={{
                        backgroundColor: '#0B2545',
                        color: '#FFFFFF',
                        py: 1.5,
                        mb: 2.5,
                        '&:hover': { backgroundColor: '#06152B' },
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        fontFamily: 'Arial, sans-serif',
                        borderRadius: '50px',
                        boxShadow: 'rgba(0, 0, 0, 0.5) 0px 4px 18px 0px'
                      }}
                    >
                      {identifyLoading ? 'Continuing...' : 'Continue'}
                    </Button>
                  </form>

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                    <Divider sx={{ flex: 1, borderColor: '#e2e8f0' }} />
                    <Typography variant="caption" sx={{ mx: 1, color: '#94a3b8', fontWeight: 600 }}>OR</Typography>
                    <Divider sx={{ flex: 1, borderColor: '#e2e8f0' }} />
                  </Stack>

                  <Button
                    onClick={handleGoogle}
                    startIcon={<Box component="img" src={googleIcon} alt="google" sx={{ width: 18, height: 18 }} />}
                    variant="outlined"
                    fullWidth
                    sx={{
                      backgroundColor: '#fff',
                      color: '#334155',
                      borderColor: '#cbd5e1',
                      textTransform: 'none',
                      borderRadius: '8px',
                      py: 1,
                      fontWeight: 500,
                      '&:hover': { backgroundColor: '#f8fafc', borderColor: '#94a3b8' }
                    }}
                  >
                    Continue with Google
                  </Button>
                </>
              ) : (
                <form onSubmit={onStep2Submit}>
                  {/* Read-only identifier with Not you? Change link */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                      p: 1.5,
                      backgroundColor: '#F8FAFC',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#0F172A',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          fontFamily: 'Arial, sans-serif',
                        }}
                      >
                        {resolvedIdentifier}
                      </Typography>
                      {authMethod === 'directory' && (
                        <Chip
                          label="AD"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '11px',
                            fontWeight: 700,
                            backgroundColor: '#E0E7FF',
                            color: '#3730A3',
                          }}
                        />
                      )}
                    </Stack>
                    <Button
                      type="button"
                      variant="text"
                      onClick={handleBackToStep1}
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#1F6FEB',
                        p: 0,
                        minWidth: 'auto',
                        fontFamily: 'Arial, sans-serif',
                        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                      }}
                    >
                      Not you? Change
                    </Button>
                  </Box>

                  {/* Readonly username input in form for password managers */}
                  <input
                    type="text"
                    name="username"
                    value={resolvedIdentifier}
                    readOnly
                    autoComplete="username"
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0 }}
                  />

                  {/* Password method label */}
                  {authMethod === 'directory' ? (
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: '#334155',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontFamily: 'Arial, sans-serif',
                      }}
                    >
                      Sign in with your Active Directory password
                      <Chip
                        label="AD"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '10px',
                          fontWeight: 700,
                          backgroundColor: '#E0E7FF',
                          color: '#3730A3',
                        }}
                      />
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: '#334155',
                        fontWeight: 500,
                        fontFamily: 'Arial, sans-serif',
                      }}
                    >
                      Enter your password
                    </Typography>
                  )}

                  <TextField
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    inputProps={{
                      autoComplete: 'current-password',
                      'aria-label': authMethod === 'directory' ? 'Sign in with your Active Directory password' : 'Enter your password',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#1F6FEB' } }} />}
                      label={<Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Arial, sans-serif' }}>Remember me</Typography>}
                    />
                  </Stack>

                  {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#0B2545',
                      color: '#FFFFFF',
                      py: 1.5,
                      mb: 2.5,
                      '&:hover': { backgroundColor: '#06152B' },
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      fontFamily: 'Arial, sans-serif',
                      borderRadius: '50px',
                      boxShadow: 'rgba(0, 0, 0, 0.5) 0px 4px 18px 0px'
                    }}
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </form>
              )}
            </>
          )}
        </Box>
      </Box>


      {/* Right side: Blue Gradient Banner with Feature Grid */}
      <Box
        sx={{
          flex: { xs: 'none', md: '1' },
          background: 'linear-gradient(145deg, #0B2545 0%, #1E293B 100%)',
          px: { xs: 3, sm: 4, md: 5, lg: 6 },
          py: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          color: '#ffffff',
          position: 'relative',
        }}
      >
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'space-between'
        }}>
          {/* Main Hero Text (referenced from screenshot) */}
          <Box sx={{ mb: 4, mt: 2 }}>
            <Typography
              component="h1"
              sx={{
                fontWeight: '700 !important',
                fontFamily: 'Arial, sans-serif',
                fontSize: { xs: '28px !important', md: '36px !important' },
                color: '#FFFFFF !important',
                lineHeight: '1.25 !important',
                mb: 0.5
              }}
            >
              Intelligent Zero
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: '700 !important',
                fontFamily: 'Arial, sans-serif',
                fontSize: { xs: '28px !important', md: '36px !important' },
                color: '#1F6FEB !important',
                lineHeight: '1.25 !important',
                mb: 2
              }}
            >
              Trust Access Management
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontFamily: 'Arial, sans-serif',
                color: '#E4E9F0',
                maxWidth: '500px',
                lineHeight: 1.5
              }}
            >
              Adaptive authentication driven by real-time risk signals.
              Frictionless SSO and enterprise identity provider integration.
            </Typography>
          </Box>

          {/* Feature Cards Grid (Centered) */}
          <Box sx={{ my: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              {[
                { title: "Multi-Factor Authentication", desc: "Protect every sign-in with flexible MFA methods, including authenticator apps, OTPs and biometrics.", icon: <VerifiedUserIcon sx={{ color: '#1F6FEB', fontSize: 20 }} /> },
                { title: "Identity Provider Integration", desc: "Connect your existing identity providers and centralize secure access across your organization.", icon: <ShieldIcon sx={{ color: '#1F6FEB', fontSize: 20 }} /> },
                { title: "Single Sign-On", desc: "Give users seamless access to approved applications with secure, passwordless SSO experiences.", icon: <FingerprintIcon sx={{ color: '#1F6FEB', fontSize: 20 }} /> },
                { title: "Adaptive Authentication", desc: "Apply intelligent access decisions using user, device, location and risk-based security signals.", icon: <BlockIcon sx={{ color: '#1F6FEB', fontSize: 20 }} /> },
                { title: "Access Policies", desc: "Define and enforce granular authentication policies for users, groups, applications and tenants.", icon: <FilterListIcon sx={{ color: '#1F6FEB', fontSize: 20 }} /> },
                { title: "Identity Audit & Reports", desc: "Track authentication activity, review access events and maintain visibility across your identity landscape.", icon: <DescriptionOutlinedIcon sx={{ color: '#1F6FEB', fontSize: 20 }} /> },
              ].map((card, index) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: '#1E293B',
                    border: '1px solid #475569',
                    borderRadius: '10px',
                    p: 2.5,
                    boxShadow: 'rgba(0, 0, 0, 0.15) 10px 0px 30px 0px',
                    transition: 'transform 0.3s ease, border-color 0.3s ease',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 2,
                    '&:hover': {
                      borderColor: '#6B7A90',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  <Box sx={{
                    backgroundColor: 'rgba(31, 111, 235, 0.1)',
                    p: 1,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {card.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, color: '#FFFFFF', fontSize: '15px', mb: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Arial, sans-serif', color: '#CBD5E1', lineHeight: 1.5, display: 'block', fontSize: '13px' }}>
                      {card.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Powered By (Bottom) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4, mb: 2 }}>
            <Typography sx={{ fontSize: 13, color: '#6B7A90', fontWeight: 600, fontFamily: 'Arial, sans-serif', letterSpacing: '0.5px' }}>
              POWERED BY
            </Typography>
            <img
              src={medgeDarkBgSidebarF}
              alt="Medge.io"
              style={{
                width: 75,
                height: "auto",
                display: "block"
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ShieldIcon(props) {
  return <SecurityIcon {...props} />;
}